/**
 * Шапка карточки игрока и плитка с числом.
 *
 * Вынесено из страницы: страница и без того длинная, а вид человека —
 * отдельная вещь от списков под ним.
 */

import type { ReactNode } from 'react';
import { tierLabel, type PlayerStats } from '@ak/shared';

import { formatRelative } from '../lib/format.ts';
import { navigate } from '../lib/router.ts';
import { Badge, Button, Panel } from './ui.tsx';

/** Цвет тира — им же покрашен знак игрока, как везде в админке. */
function tierColor(tier: number): string {
  return tier === 0 ? 'var(--tier-none)' : `var(--tier-${String(tier)})`;
}

/**
 * Шапка карточки: аватарка, ник и всё, чем человек подписан.
 *
 * Аватарка приходит из Discord и лежит в анкете; пока её нет, на её месте
 * буква на цвете тира. Картинка кладётся поверх знака, а не вместо него —
 * не загрузись она, клетка не схлопнется.
 */
export function PlayerHero({ player }: { player: PlayerStats }): ReactNode {
  const color = tierColor(player.tier);

  return (
    <Panel className="mb-4 overflow-hidden">
      <div className="relative p-5">
        {/* Мягкое свечение в углу — тот же приём, что у пьедестала. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-16 -right-10 size-48 rounded-full blur-3xl"
          style={{ background: `color-mix(in oklab, ${color} 22%, transparent)` }}
        />

        <div className="relative flex flex-wrap items-center gap-4">
          <span
            aria-hidden="true"
            className="relative grid size-16 shrink-0 place-items-center overflow-hidden rounded-2xl text-2xl font-semibold"
            style={{
              color,
              background: `color-mix(in oklab, ${color} 18%, transparent)`,
              border: `1px solid color-mix(in oklab, ${color} 45%, transparent)`,
              fontFamily: 'var(--font-rounded)',
            }}
          >
            {player.gameNick.slice(0, 1).toUpperCase()}
            {player.avatarUrl !== null && (
              <img src={player.avatarUrl} alt="" className="absolute inset-0 size-full object-cover" />
            )}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-semibold tracking-tight">{player.gameNick}</h1>
              <span
                className="rounded-md px-2 py-0.5 text-xs font-semibold"
                style={{ color, background: `color-mix(in oklab, ${color} 15%, transparent)` }}
              >
                {player.tier === 0 ? 'без тира' : tierLabel(player.tier)}
              </span>
              {player.leftAt !== null && <Badge tone="danger">вышел с сервера</Badge>}
            </div>

            <div
              className="mt-1 flex flex-wrap items-center gap-x-2 text-sm"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>{player.staticId}</span>
              {player.discordName !== null && <span>· {player.discordName}</span>}
              <span>· анкета обновлена {formatRelative(player.updatedAt)}</span>
            </div>
          </div>

          <Button
            variant="secondary"
            onClick={() => {
              navigate('/stats');
            }}
          >
            К статистике
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** Плитка с числом: из них собраны обе полосы показателей. */
export function Tile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  // Явное `| undefined`: при exactOptionalPropertyTypes необязательное поле и
  // поле со значением undefined — разные вещи.
  hint?: string | undefined;
  tone?: string | undefined;
}): ReactNode {
  return (
    <div
      className="rounded-xl p-4"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
    >
      <div className="text-xs tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
        {label}
      </div>
      <div
        className="mt-1.5 text-2xl font-semibold tabular-nums"
        style={{ color: tone ?? 'var(--text-primary)', fontFamily: 'var(--font-rounded)' }}
      >
        {value}
      </div>
      {hint !== undefined && (
        <div className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>
          {hint}
        </div>
      )}
    </div>
  );
}
